import { getLuminance } from 'polished'
import type { BannerKind } from 'LuxKit/Banners/types'
import { getLabItemsSortedByRank } from 'lib/lab/rankedLabItems'
import { removeRegionCode } from 'lib/string/regionUtils'

const DEFAULT_HEADER_BANNER_BACKGROUND = '#CAFFEB'
const DEFAULT_HEADER_BANNER_TEXT_COLOR = '#363A45'
const DEFAULT_HEADER_BANNER_CTA_PRIMARY_TEXT_COLOR = '#FFFFFF'
const DEFAULT_HEADER_BANNER_KIND: App.LabHeaderBannerResult['kind'] = 'default'
const MIDDLE_GREY_LUMINANCE = getLuminance('#777')

const LAB_HEADER_KIND_TO_BANNER_KIND: Record<App.LabHeaderBannerResult['kind'], BannerKind> = {
  'informational-primary': 'informational',
  'promotional-primary': 'promotional',
  'default': 'default',
  'promotional-primary-accent': 'branded',
  'promotional-secondary': 'promotional',
  success: 'success',
  warning: 'warning',
  critical: 'critical',
  'lux-plus': 'lux-plus',
}

function isLabHeaderKind(kind: string): kind is App.LabHeaderBannerResult['kind'] {
  return Object.hasOwn(LAB_HEADER_KIND_TO_BANNER_KIND, kind)
}

export const mapLabHeaderBannerKind = (kind: string): BannerKind => {
  return isLabHeaderKind(kind) ? LAB_HEADER_KIND_TO_BANNER_KIND[kind] : 'default'
}

export type TakeoverBannerLocation = 'search' | 'offer' | 'generic'

export const getTakeoverBannerLocation = (
  isSearchPage: boolean,
  isOfferPage: boolean,
): TakeoverBannerLocation => {
  if (isSearchPage) {
    return 'search'
  }
  if (isOfferPage) {
    return 'offer'
  }
  return 'generic'
}

export const getRenderableHeaderBanners = (
  headerBanners: App.LabHeaderBannersResult | undefined,
  dismissedIds: Set<string>,
): Array<App.LabHeaderBannerResult> => {
  if (!headerBanners?.banners?.length) {
    return []
  }

  return getLabItemsSortedByRank(headerBanners.banners)
    .filter(banner => !dismissedIds.has(banner.id))
}

export const getRenderableHeaderBanner = (
  headerBanners: App.LabHeaderBannersResult | undefined,
  dismissedIds: Set<string>,
): App.LabHeaderBannerResult | undefined => {
  return getRenderableHeaderBanners(headerBanners, dismissedIds)[0]
}

export interface LuxPlusRegionOverride {
  isOutsideMemberRegion: boolean
  memberRegion: string
  switchRegionLabel: string
}

export const getHeaderBannerPresentation = (
  banner: App.LabHeaderBannerResult,
  isMobile: boolean,
  luxPlusRegionOverride?: LuxPlusRegionOverride,
): {
  kind: BannerKind
  isCustomAppearance: boolean
  description: string
  cta?: { label: string, path: string, style: 'primary' | 'secondary' | 'tertiary' }
  isLuxPlusRegionSwitch: boolean
  background: { mode: 'solid' | 'gradient', value: string } | { mode: 'image', imageId: string }
  textColor: string
  ctaPrimaryTextColor: string
  iconImageId?: string
} => {
  const description = isMobile && banner.description.mobile ? banner.description.mobile : banner.description.desktop
  const ctaItem = isMobile
    ? (banner.cta?.mobile ?? banner.cta?.desktop)
    : banner.cta?.desktop
  let cta = ctaItem
    ? {
        ...ctaItem,
        style: ctaItem.style ?? ((ctaItem as { outlined?: boolean }).outlined ? 'secondary' : 'tertiary'),
      }
    : undefined

  const isLuxPlusRegionSwitch = !!banner.isLuxPlusBanner &&
    !!luxPlusRegionOverride?.isOutsideMemberRegion &&
    !!cta

  if (isLuxPlusRegionSwitch && cta) {
    cta = {
      ...cta,
      label: luxPlusRegionOverride.switchRegionLabel,
      path: `/${luxPlusRegionOverride.memberRegion}${removeRegionCode(cta.path, luxPlusRegionOverride.memberRegion)}`,
    }
  }

  const textColor = banner.textColor || DEFAULT_HEADER_BANNER_TEXT_COLOR
  const ctaPrimaryTextColor = banner.ctaPrimaryTextColor || DEFAULT_HEADER_BANNER_CTA_PRIMARY_TEXT_COLOR

  const background = banner.background.mode === 'image'
    ? {
        mode: 'image' as const,
        imageId: banner.background.imageId,
      }
    : {
        mode: banner.background.mode,
        value: banner.background.value || DEFAULT_HEADER_BANNER_BACKGROUND,
      }

  const isLightText = getLuminance(textColor) > MIDDLE_GREY_LUMINANCE

  return {
    kind: banner.isCustomAppearance
      ? (isLightText ? 'none-inverse' : 'none')
      : mapLabHeaderBannerKind(banner.kind || DEFAULT_HEADER_BANNER_KIND),
    isCustomAppearance: banner.isCustomAppearance ?? false,
    description,
    cta,
    isLuxPlusRegionSwitch,
    background,
    textColor,
    ctaPrimaryTextColor,
    iconImageId: banner.iconImageId?.trim() || undefined,
  }
}
